import { supabase } from "~/integrations/supabase/client";

// New parallel inventory module — product master data (sección Productos > Lista maestra).
export type InvProductCategory =
  | "materia_prima"
  | "aditivo_insumo"
  | "material_empaque"
  | "producto_terminado";

export interface InvProductMaster {
  id: string;
  source_kind: "finished_good" | "raw_material" | "oil";
  code: string | null;
  name: string;
  category: InvProductCategory | null;
  stock_uom: string;
  tracking: string;
  is_storable: boolean;
  density_kg_per_liter: number | null;
  standard_cost: number | null;
  barcode: string | null;
  total_stock: number;
  is_active: boolean;
  image_url: string | null;
  last_sale_price: number | null;
  company_id: string | null;
  company_name: string | null;
}

export interface InvCompany {
  id: string;
  name: string;
}

export const INV_CATEGORY_LABELS: Record<InvProductCategory, string> = {
  materia_prima: "Materia Prima",
  aditivo_insumo: "Aditivos e insumos",
  material_empaque: "Material de Empaque",
  producto_terminado: "Productos Terminados",
};

export const INV_CATEGORY_OPTIONS: { value: InvProductCategory; label: string }[] = [
  { value: "materia_prima", label: "Materia Prima" },
  { value: "aditivo_insumo", label: "Aditivos e insumos" },
  { value: "material_empaque", label: "Material de Empaque" },
  { value: "producto_terminado", label: "Productos Terminados" },
];

export const INV_TRACKING_LABELS: Record<string, string> = {
  none: "Ninguno",
  lot: "Lote",
  serial: "Serie",
};

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn as never, args as never);
  if (error) throw error;
  return data as unknown as T;
}

export interface InvProductStockLocation {
  location_code: string;
  location_name: string;
  quantity: number;
  reserved: number;
  value: number | null;
}

export interface InvProductLot {
  name: string;
  expiration_date: string | null;
  ref: string | null;
}

export interface InvProductDetail {
  product: InvProductMaster & { total_value: number | null; extra: Record<string, unknown> | null };
  stock_by_location: InvProductStockLocation[];
  lots: InvProductLot[];
}

export interface CreateInvProductInput {
  source_kind: "finished_good" | "raw_material" | "oil";
  code?: string;
  name: string;
  category?: InvProductCategory | null;
  stock_uom?: string;
  tracking?: string;
  standard_cost?: number | null;
  density_kg_per_liter?: number | null;
}

export const inventoryProductService = {
  getMaster: (opts: { search?: string; category?: InvProductCategory | null }) =>
    rpc<InvProductMaster[]>("get_inv_product_master", {
      p_search: opts.search?.trim() || null,
      p_category: opts.category || null,
    }),

  setCategory: (productId: string, category: InvProductCategory) =>
    rpc<void>("set_inv_product_category", {
      p_product_id: productId,
      p_category: category,
    }),

  getDetail: (productId: string) =>
    rpc<InvProductDetail>("get_inv_product_detail", { p_id: productId }),

  getCompanies: () => rpc<InvCompany[]>("get_inv_companies", {}),

  setCompany: (productId: string, companyId: string) =>
    rpc<void>("set_inv_product_company", {
      p_product_id: productId,
      p_company_id: companyId,
    }),

  create: (input: CreateInvProductInput) =>
    rpc<string>("create_inv_product", {
      p_source_kind: input.source_kind,
      p_code: input.code?.trim() || null,
      p_name: input.name.trim(),
      p_category: input.category ?? null,
      p_stock_uom: input.stock_uom?.trim() || "unit",
      p_tracking: input.tracking || "none",
      p_standard_cost: input.standard_cost ?? null,
      p_density_kg_per_liter: input.density_kg_per_liter ?? null,
    }),
};

export const INV_SOURCE_KIND_LABELS: Record<string, string> = {
  finished_good: "Producto Terminado",
  raw_material: "Materia Prima",
  oil: "Aceite",
};
